import type {ReactNode} from "react";
import {createContext, useCallback, useContext, useEffect, useMemo, useRef, useState} from "react";
import {useUnsavedChangesGuard} from "~/hooks/useUnsavedChangesGuard";

interface RowEngagementUnsavedChangesContextValue {
    /** The active edit panel reports whether it currently holds unsaved edits. */
    reportDirty: (isDirty: boolean) => void;
    /**
     * Route a close/dismiss action through the guard. If the active panel is
     * dirty this opens the confirmation modal and only runs `performDismiss`
     * once the user confirms; otherwise it runs immediately.
     */
    requestDismiss: (performDismiss: () => void) => void;
}

const RowEngagementUnsavedChangesContext = createContext<RowEngagementUnsavedChangesContextValue | null>(null);

/**
 * Provides a single unsaved-changes guard shared by the row-engagement panel
 * chrome (close buttons, Escape/backdrop) and the active edit panel. Also wires
 * browser back/forward/refresh blocking while a dirty panel is open.
 *
 * Render this once around the panel subtree; it renders the confirmation modal.
 */
export function RowEngagementUnsavedChangesProvider({children}: {children: ReactNode}) {
    const [isDirty, setIsDirty] = useState(false);
    const pendingDismissRef = useRef<(() => void) | null>(null);

    const onDiscard = useCallback(() => {
        const action = pendingDismissRef.current;
        pendingDismissRef.current = null;
        setIsDirty(false);
        if (action !== null) {
            action();
        }
    }, []);

    const {requestClose, modal} = useUnsavedChangesGuard({isDirty, onDiscard});

    const reportDirty = useCallback((dirty: boolean) => {
        setIsDirty(dirty);
    }, []);

    const requestDismiss = useCallback(
        (performDismiss: () => void) => {
            pendingDismissRef.current = performDismiss;
            requestClose();
        },
        [requestClose],
    );

    const value = useMemo<RowEngagementUnsavedChangesContextValue>(
        () => ({reportDirty, requestDismiss}),
        [reportDirty, requestDismiss],
    );

    return (
        <RowEngagementUnsavedChangesContext.Provider value={value}>
            {children}
            {modal}
        </RowEngagementUnsavedChangesContext.Provider>
    );
}

/**
 * Returns a `dismiss(performDismiss)` that routes a close action through the
 * shared guard. Falls back to an immediate dismiss if no provider is present.
 */
export function useGuardedPanelDismiss(): (performDismiss: () => void) => void {
    const context = useContext(RowEngagementUnsavedChangesContext);
    if (context === null) {
        return (performDismiss) => {
            performDismiss();
        };
    }
    return context.requestDismiss;
}

/**
 * Reports the active edit panel's dirty state to the shared guard. Clears the
 * dirty flag on unmount so a subsequently opened (clean) panel isn't guarded.
 */
export function useReportPanelDirty(isDirty: boolean): void {
    const context = useContext(RowEngagementUnsavedChangesContext);
    const reportDirty = context?.reportDirty;

    useEffect(() => {
        if (reportDirty === undefined) {
            return;
        }
        reportDirty(isDirty);
    }, [reportDirty, isDirty]);

    useEffect(() => {
        if (reportDirty === undefined) {
            return;
        }
        return () => {
            reportDirty(false);
        };
    }, [reportDirty]);
}
